import type { MinistriesModel } from '@/features/master-table/models';
import { getMinistryName } from '@/features/mold/utils';
import type {
  OrganizationsModel,
  UsersModel,
} from '@/features/tenant-manager/models';
import { t } from '@/i18n';
import { NEST_ITEM_CONNECTOR, findUpperRecursive } from '@/utils';
import type { z } from 'zod';
import {
  type FundSheetModel,
  type ProjectBaseModel,
  ProjectRelationTypeLabelModifier,
} from '../../projects/_models/project';
import { formatLegalBasis } from './legal-bases';

type Project = z.infer<typeof FundSheetModel> | undefined;

export const diffRenderValueFormatter = {
  ministryId: (
    value: string,
    ministries: z.infer<typeof MinistriesModel> | undefined,
  ) =>
    getMinistryName({
      targetId: value,
      ministries,
    }) ?? '',
  organizationId: (
    value: string,
    organizations: z.infer<typeof OrganizationsModel> | undefined,
  ) =>
    findUpperRecursive(value, organizations ?? [])
      .reverse()
      .map((d) => d.name)
      .join(NEST_ITEM_CONNECTOR),
  otherResponsibleOrganizationIds: (
    values: string[],
    organizations: z.infer<typeof OrganizationsModel> | undefined,
  ) =>
    values
      .map((value) => {
        return findUpperRecursive(value, organizations ?? [])
          .reverse()
          .map((d) => d.name)
          .join(NEST_ITEM_CONNECTOR);
      })
      .join('\n'),
  relatedOrganizationIds: (
    values: string[],
    organizations: z.infer<typeof OrganizationsModel> | undefined,
  ) =>
    values
      .map((value) => {
        return findUpperRecursive(value, organizations ?? [])
          .reverse()
          .map((d) => d.name)
          .join(NEST_ITEM_CONNECTOR);
      })
      .join('\n'),
  peopleInChargeIds: (
    value: string[],
    users: z.infer<typeof UsersModel> | undefined,
  ) =>
    users
      ?.flatMap((user) => (value.includes(user.id) ? [user.name] : []))
      .join('\n') ?? '',
  businessManagerIds: (
    value: string[],
    users: z.infer<typeof UsersModel> | undefined,
  ) =>
    users
      ?.flatMap((user) => (value.includes(user.id) ? [user.name] : []))
      .join('\n') ?? '',
  startYear: (project: Project | null | undefined) => {
    return (
      project?.startYear?.toString() ??
      (project?.isUnknownStart ? t('unknown-start') : t('blank'))
    );
  },
  endYear: (project: Project | null | undefined) => {
    return (
      project?.endYear?.toString() ??
      (project?.isNoScheduledEnd ? t('no-scheduled-end') : t('blank'))
    );
  },
  relatedProjects: (
    value: z.infer<typeof ProjectBaseModel.shape.relatedProjects>,
  ) => {
    return value
      .map(
        (relatedProject) =>
          `${
            ProjectRelationTypeLabelModifier[relatedProject.relationType]
          }: ${relatedProject.name}`,
      )
      .join('\n');
  },
  legalBases: (
    value: z.infer<typeof FundSheetModel.shape.legalBases> | undefined,
  ) => {
    return value?.map(formatLegalBasis).join('\n') ?? '';
  },
  plans: (value: z.infer<typeof FundSheetModel.shape.plans>) =>
    (value ?? []).map((v) => `${v.contents ?? ''}\n${v.url ?? ''}`).join('\n'),
} as const;
